import { useContext } from 'react'
import type { CSSProperties } from 'react'
import { LangueCourante } from '../i18n/langue'

/**
 * La marque Sanabil (سنابل, « les épis ») : trois épis liés par un nœud doré — la piste
 * « Gerbe », approuvée le 2026-09-25. Chaque épi figure l'une des trois activités vendues
 * séparément : grandes cultures, arboriculture, élevage.
 *
 * Le symbole est dessiné ici plutôt que chargé depuis un `.svg` : un fichier passé à
 * `<img>` n'atteint pas les polices de la page, et le nom retomberait sur une police
 * système. Le symbole prend `currentColor` : sa couleur suit le thème par la CSS
 * (`--accent` en clair comme en sombre), sans variante à tenir à la main.
 *
 * Le favicon `public/favicon.svg` et les icônes du mobile reprennent le même tracé ; les
 * redessiner ensemble.
 */

const NOM_LATIN = 'Sanabil'
const NOM_ARABE = 'سنابل'

/** Couleur du nœud : l'or du soleil, constant d'un thème à l'autre. */
const OR = '#E8B23A'

/** Un épi droit, pied en (32, 58) : une tige, deux rangs de grains, un grain de tête. */
function Epi() {
  return (
    <>
      <path d="M32 58V22" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" />
      <ellipse cx={28} cy={29} rx={3.2} ry={6} transform="rotate(-20 28 29)" />
      <ellipse cx={36} cy={29} rx={3.2} ry={6} transform="rotate(20 36 29)" />
      <ellipse cx={28} cy={20} rx={3.2} ry={6} transform="rotate(-20 28 20)" />
      <ellipse cx={36} cy={20} rx={3.2} ry={6} transform="rotate(20 36 20)" />
      <ellipse cx={32} cy={12} rx={3.2} ry={6} />
    </>
  )
}

/**
 * Sous 32 px, les grains d'un épi se confondent : chaque épi se réduit à sa tige et à un
 * seul grain, plus épais. C'est le dessin du favicon.
 */
function EpiSimplifie() {
  return (
    <>
      <path d="M32 58V26" fill="none" stroke="currentColor" strokeWidth={5} strokeLinecap="round" />
      <ellipse cx={32} cy={17} rx={6} ry={10} />
    </>
  )
}

export function Gerbe({ taille = 32, style }: { taille?: number; style?: CSSProperties }) {
  const simplifie = taille < 32
  const EpiDessine = simplifie ? EpiSimplifie : Epi
  return (
    <svg width={taille} height={taille} viewBox="0 0 64 64" fill="currentColor" aria-hidden="true" focusable="false" style={style}>
      <g transform={simplifie ? 'translate(32 55) scale(0.8) translate(-32 -58)' : 'translate(32 56) scale(0.85) translate(-32 -58)'}>
        <g transform="rotate(-40 32 58)">
          <EpiDessine />
        </g>
        <g transform="rotate(40 32 58)">
          <EpiDessine />
        </g>
        <EpiDessine />
        {simplifie ? <rect x={22} y={43} width={20} height={7} rx={3.5} fill={OR} /> : <rect x={24} y={45} width={16} height={5} rx={2.5} fill={OR} />}
      </g>
    </svg>
  )
}

/**
 * Le symbole et le nom, dans les deux écritures. Le nom de la langue courante passe en
 * premier ; l'autre écriture le suit en plus petit. Le symbole n'est jamais inversé en RTL :
 * la gerbe est symétrique, seul l'ordre symbole / texte suit la direction de la page.
 *
 * - `barre` : en-tête de la barre latérale, sur fond `--nav-bg`.
 * - `empilee` : écran de connexion, symbole au-dessus du nom.
 */
export function Marque({ variante }: { variante: 'barre' | 'empilee' }) {
  // La langue seule (i18n/langue.ts) : le site vitrine porte la marque sans le dictionnaire.
  const language = useContext(LangueCourante)
  const principal = language === 'ar' ? NOM_ARABE : NOM_LATIN
  const second = language === 'ar' ? NOM_LATIN : NOM_ARABE
  const langueSeconde = language === 'ar' ? undefined : 'ar'

  if (variante === 'empilee') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        <Gerbe taille={88} style={{ color: 'var(--accent)' }} />
        <span style={{ fontFamily: 'var(--f-display)', fontSize: 34, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1, color: 'var(--ink)' }}>{principal}</span>
        <span lang={langueSeconde} style={{ fontFamily: language === 'ar' ? 'var(--f-display)' : 'var(--f-ar)', fontSize: 17, fontWeight: 600, color: 'var(--muted)' }}>
          {second}
        </span>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Gerbe taille={40} style={{ color: '#3FBE95', flex: '0 0 auto' }} />
      <span>
        <b style={{ fontSize: 19 }}>{principal}</b>
        <span className="tag" lang={langueSeconde} style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12, fontFamily: language === 'ar' ? 'var(--f-display)' : 'var(--f-ar)' }}>
          {second}
        </span>
      </span>
    </div>
  )
}
